import { useState } from 'react';

import { Button } from '@/ui/button/Button';
import { Input } from '@/ui/Input';
import { Modal } from '@/ui/Modal';
import { Textarea } from '@/ui/Textarea';

import type { GoalStartRequest } from '../../stores/goal';

const DEFAULT_MAX_ITERATIONS = 20;

interface StartGoalModalProps {
  onClose: () => void;
  /** False when the request could not be sent; the form then stays open. */
  onStart: (goal: GoalStartRequest) => boolean;
}

/**
 * Start a `/goal`: what should be true when it is done, how many iterations it may take, an
 * optional budget, and whether it may stop to ask. Off, the agent decides for itself and says
 * what it assumed — the run never waits on anybody.
 */
export function StartGoalModal({ onClose, onStart }: StartGoalModalProps) {
  const [objective, setObjective] = useState('');
  const [maxIterations, setMaxIterations] = useState(String(DEFAULT_MAX_ITERATIONS));
  const [budget, setBudget] = useState('');
  const [interactive, setInteractive] = useState(false);

  const iterations = Number(maxIterations);
  const dollars = budget.trim() === '' ? undefined : Number(budget.replace(/^\$/, ''));
  const valid =
    objective.trim() !== '' &&
    Number.isInteger(iterations) &&
    iterations > 0 &&
    (dollars === undefined || (Number.isFinite(dollars) && dollars > 0));

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!valid) return;
    const sent = onStart({
      objective: objective.trim(),
      maxIterations: iterations,
      budgetMicroUsd: dollars === undefined ? undefined : Math.round(dollars * 1_000_000),
      interactive,
    });
    if (sent) onClose();
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Start a goal"
      description="The agent works in iterations until an independent check agrees the goal is met."
    >
      <form onSubmit={submit} className="flex flex-col gap-4 px-6 pb-6">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="goal-objective" className="text-xs font-medium text-fg">
            Goal
          </label>
          <Textarea
            id="goal-objective"
            rows={3}
            value={objective}
            onChange={(e) => setObjective(e.target.value)}
            placeholder="What should be true when it is done — e.g. every test in the api crate passes"
            autoFocus
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="goal-max" className="text-xs font-medium text-fg">
              Iterations at most
            </label>
            <Input
              id="goal-max"
              inputMode="numeric"
              value={maxIterations}
              onChange={(e) => setMaxIterations(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="goal-budget" className="text-xs font-medium text-fg">
              Budget (optional)
            </label>
            <Input
              id="goal-budget"
              inputMode="decimal"
              value={budget}
              onChange={(e) => setBudget(e.target.value)}
              placeholder="$ no limit"
            />
          </div>
        </div>
        <label className="flex items-start gap-2 text-xs text-fg-secondary">
          <input
            type="checkbox"
            checked={interactive}
            onChange={(e) => setInteractive(e.target.checked)}
            className="mt-0.5"
          />
          <span>
            <span className="font-medium text-fg">Ask me when unsure</span>
            <br />
            The goal pauses on a question until you answer. Off, the agent decides and notes what it assumed.
          </span>
        </label>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={!valid}>
            Start goal
          </Button>
        </div>
      </form>
    </Modal>
  );
}
